import { apiClient } from "~/lib/client";
import { handleApiError, handleResponse } from "~/utils/response";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Prescription, PrescriptionResponse } from "../types";
import { patientLogsKeys } from "../queries/keys";
import { toast } from "sonner";
import { getApiProgram } from "~/lib/utils";
import { tasksKeys } from "~/features/dashboard/api/tasks/keys";
import { quickActionsKeys } from "../../../quick-actions/api/queries";
import { encountersKeys } from "../../../encounters/api/queries";
    
export const useCreatePrescriptionMutation = (encounterId: string) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (data: Prescription) => {
            try {
                const response = await apiClient(`/admin/encounters/${encounterId}/tasks`, {
                    method: 'POST',
                    body: JSON.stringify(data)
                });
                const result = await handleResponse<PrescriptionResponse>(response);

                if (!result.success) {
                    throw new Error(result.error);
                }

                return result.data;
            } catch (error) {
                handleApiError(error);
                throw error;
            }
        },
        onSuccess: async (_, variables) => {
            const patientId = variables.patient_id;
            
            // Invalidate all relevant queries and wait for them to complete
            const invalidations = [];
            
            if (patientId) {
                invalidations.push(
                    queryClient.invalidateQueries({ 
                        predicate: (query) => {
                            const queryKey = query.queryKey;
                            return (
                                queryKey[0] === 'patients' &&
                                queryKey[1] === 'medication' &&
                                queryKey[2] === patientId &&
                                queryKey[4] === 'prescribed'
                            );
                        }
                    })
                );
            }
            
            // Invalidate quick actions queries
            invalidations.push(
                queryClient.invalidateQueries({ 
                    queryKey: [...quickActionsKeys.all, "encounters", "today"] 
                })
            );
            
            invalidations.push(
                queryClient.invalidateQueries({ 
                    queryKey: [...encountersKeys.all, "upcoming"] 
                })
            );
            
            // Invalidate encounter queries to refresh recommendedTasks
            invalidations.push(
                queryClient.invalidateQueries({ 
                    queryKey: encountersKeys.all 
                })
            );
            
            // Invalidate specific encounter by ID if we have it
            if (encounterId) {
                invalidations.push(
                    queryClient.invalidateQueries({ 
                        queryKey: encountersKeys.encounterById(encounterId)
                    })
                );
            }
            
            // Wait for all invalidations to complete
            await Promise.all(invalidations);
        }
    });
};

export const useAddOrUpdateNoteMutation = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ encounterId, subjective, objective, assessment, plan }: { encounterId: string; subjective: string; objective: string; assessment: string; plan: string; patientId: string }) => {
            try {
                const response = await apiClient(`/admin/encounters/${encounterId}/notes`, {
                    method: 'POST',
                    body: JSON.stringify({ subjective, objective, assessment, plan })
                });
                const result = await handleResponse<{ success: boolean; data: any; error?: string }>(response);

                if (!result.success) {
                    throw new Error(result.error);
                }

                return result.data;
            } catch (error) {
                handleApiError(error);
                throw error;
            }
        },
        onSuccess: (_data, variables) => {
            // Invalidate and refetch the notes query for this patient
            queryClient.invalidateQueries({ 
                queryKey: patientLogsKeys.notes(variables.patientId) 
            });
            // Also invalidate tasks query since notes come from getTasksByEncounterIdQuery
            queryClient.invalidateQueries({ 
                queryKey: tasksKeys.getTasksByEncounterId(variables.encounterId) 
            });
            // Invalidate quick actions encounters queries
            queryClient.invalidateQueries({ 
                queryKey: [...quickActionsKeys.all, "encounters", "today"] 
            });
            // Invalidate upcoming encounters queries
            queryClient.invalidateQueries({ 
                queryKey: [...encountersKeys.all, "upcoming"] 
            });
            // Invalidate all encounters queries
            queryClient.invalidateQueries({ 
                queryKey: encountersKeys.all 
            });
        }
    });
}

export const useSendActionNeededMutation = () => {
    return useMutation({
        mutationFn: async ({ patient_id }: { patient_id: string }) => {
            try {
                const response = await apiClient("/admin/patients/action-needed", {
                    method: "POST",
                    body: JSON.stringify({ patient_id }),
                });
                const result = await handleResponse<{ success: boolean; data: any; error?: string }>(response);

                if (!result.success) {
                    throw new Error(result.error);
                }

                return result.data;
            } catch (error) {
                handleApiError(error);
                throw error;
            }
        }
    });
};

export const useDeletePrescriptionMutation = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async ({ task_id, encounter_id }: { task_id: string; encounter_id: string; patient_id: string; program: string }) => {
            try {
                const response = await apiClient(`/admin/encounters/${encounter_id}/tasks/${task_id}`, {
                    method: 'DELETE',
                });
                const result = await handleResponse<{ success: boolean; data: any; error?: string }>(response);

                if (!result.success) {
                    throw new Error(result.error);
                }

                return result.data;
            } catch (error) {
                handleApiError(error);
                throw error;
            }
        },
        onSuccess: (_data, variables) => {
            toast.success("Medication deleted successfully!");
            // Invalidate medication queries for this patient
            queryClient.invalidateQueries({ 
                queryKey: [...patientLogsKeys.medication(variables.patient_id), variables.program, "prescribed"]
            });
            queryClient.invalidateQueries({ 
                queryKey: [...patientLogsKeys.medication(variables.patient_id), variables.program, "active"]
            });
        },
        onError: (error) => {
            toast.error(error instanceof Error ? error.message : "Failed to delete medication");
        }
    });
};

export const useGenerateNotesMutation = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async ({ encounterId, icd10_code, program }: { encounterId: string; patientId: string; icd10_code?: string; program: string }) => {
            try {
                const apiProgram = getApiProgram(program);
                const response = await apiClient(`/admin/encounters/${encounterId}/notes/generate?program=${apiProgram}`, {
                    method: 'POST',
                    body: JSON.stringify({ icd10_code: icd10_code || "" })
                });
                const result = await handleResponse<{ success: boolean; data: any; error?: string }>(response);

                if (!result.success) {
                    throw new Error(result.error);
                }

                return result.data;
            } catch (error) {
                handleApiError(error);
                throw error;
            }
        },
        onSuccess: (_data, variables) => {
            queryClient.invalidateQueries({ 
                queryKey: patientLogsKeys.notes(variables.patientId) 
            });
            queryClient.invalidateQueries({ 
                queryKey: tasksKeys.getTasksByEncounterId(variables.encounterId) 
            });
            // Invalidate quick actions encounters queries
            queryClient.invalidateQueries({ 
                queryKey: [...quickActionsKeys.all, "encounters", "today"] 
            });
            // Invalidate upcoming encounters queries
            queryClient.invalidateQueries({ 
                queryKey: [...encountersKeys.all, "upcoming"] 
            });
        }
    });
};

export const useUpdateSessionICD10Mutation = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async ({ encounterId, icd10_code, program }: { encounterId: string; icd10_code?: string; patientId: string; program: string }) => {
            try {
                const apiProgram = getApiProgram(program);
                const response = await apiClient(`/admin/encounters/${encounterId}/session/icd10?program=${apiProgram}`, {
                    method: 'PATCH',
                    body: JSON.stringify({ icd10_code: icd10_code || "" })
                });
                const result = await handleResponse<{ success: boolean; data: any; error?: string }>(response);

                if (!result.success) {
                    throw new Error(result.error);
                }

                return result.data;
            } catch (error) {
                handleApiError(error);
                throw error;
            }
        },
        onSuccess: (_data, variables) => {
            queryClient.invalidateQueries({ 
                queryKey: patientLogsKeys.notes(variables.patientId) 
            });
            queryClient.invalidateQueries({ 
                queryKey: tasksKeys.getTasksByEncounterId(variables.encounterId) 
            });
            // Invalidate quick actions encounters queries
            queryClient.invalidateQueries({ 
                queryKey: [...quickActionsKeys.all, "encounters", "today"] 
            });
            // Invalidate upcoming encounters queries
            queryClient.invalidateQueries({ 
                queryKey: [...encountersKeys.all, "upcoming"] 
            });
        }
    });
};

export const useApproveVerificationMutation = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async ({ userId }: { userId: string }) => {
            try {
                const response = await apiClient(`/admin/verifications/${userId}/approve`, {
                    method: 'PATCH',
                });
                const result = await handleResponse<{ success: boolean; data: any; error?: string }>(response);

                if (!result.success) {
                    throw new Error(result.error);
                }

                return result.data;
            } catch (error) {
                handleApiError(error);
                throw error;
            }
        },
        onSuccess: (_data, variables) => {
            toast.success("Verification approved successfully!");
            queryClient.invalidateQueries({ 
                queryKey: patientLogsKeys.verification(variables.userId) 
            });
            queryClient.invalidateQueries({ 
                queryKey: patientLogsKeys.detail(variables.userId) 
            });
        },
        onError: (error) => {
            toast.error(error instanceof Error ? error.message : "Failed to approve verification");
        }
    });
};

export const useRejectVerificationMutation = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async ({ userId, reason }: { userId: string; reason: string }) => {
            try {
                const response = await apiClient(`/admin/verifications/${userId}/reject`, {
                    method: 'PATCH',
                    body: JSON.stringify({ reason }),
                });
                const result = await handleResponse<{ success: boolean; data: any; error?: string }>(response);

                if (!result.success) {
                    throw new Error(result.error);
                }

                return result.data;
            } catch (error) {
                handleApiError(error);
                throw error;
            }
        },
        onSuccess: (_data, variables) => {
            toast.success("Verification rejected successfully!");
            queryClient.invalidateQueries({ 
                queryKey: patientLogsKeys.verification(variables.userId) 
            });
            queryClient.invalidateQueries({ 
                queryKey: patientLogsKeys.detail(variables.userId) 
            });
        },
        onError: (error) => {
            toast.error(error instanceof Error ? error.message : "Failed to reject verification");
        }
    });
};